import i18n from '@/i18n';
import { $ResetPinia } from '@/pinia';
import { RouteNames } from '@/router/names';
import api from '@/services/api';
import { useCustomersStore } from '@/stores/customers';
import { useReferenceDataStore } from '@/stores/referenceData';
import { useSyncQueueStore } from '@/stores/syncQueue';
import { useToastStore } from '@/stores/toast';
import type { Company, User } from '@/types';
import type { UserTwoFactorSetupResponse } from '@/types/user.ts';
import { isMaintenanceError, isRateLimitError, retryAfterMs } from '@/utils/apiError';
import { getCookie, isCsrfTokenValid } from '@/utils/cookie';
import { log } from '@/utils/log';
import { clearPrivateApiCache } from '@/utils/privateApiCache';
import { QUICK_TIMER_ACTIVE_SESSION_KEY } from '@/utils/quickTimer';
import type { AxiosResponse } from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

/**
 * Persisted state key in localStorage. Exported so signOut and any external
 * cleanup (e.g. test teardown) can wipe it deterministically.
 */
export const AUTH_PERSIST_KEY = 'app.auth';

/**
 * The only answers to `/api/user` that mean "signed out": 401 (no session) and
 * Laravel's 419 (the CSRF token outlived its session). A 429, a 5xx or a network
 * error says nothing about the session.
 */
const SIGNED_OUT_STATUSES = [401, 419];

/**
 * A throttled `/api/user` (milo-api limits the whole api group per user, see
 * utils/apiError.ts) is waited out instead of read as "signed out": init() sleeps
 * for as long as Retry-After asks and tries again. At most three attempts and a
 * minute of waiting, which is one window of that per-minute limiter.
 */
const USER_RATE_LIMIT_MAX_WAIT_MS = 60_000;
const USER_RATE_LIMIT_MAX_ATTEMPTS = 3;
// For a 429 without a readable Retry-After (a proxy in front of the API, say).
const USER_RATE_LIMIT_FALLBACK_WAIT_MS = 5_000;
const USER_RATE_LIMIT_MIN_WAIT_MS = 1_000;

export const useAuthStore = defineStore(
    'auth',
    () => {
        // State
        const user = ref<User | null>(null);
        const authToken = ref<string | null>(null);
        const ready = ref(false);
        const csrfCheckInterval = ref<number | null>(null);
        const isSigningOut = ref(false); // Prevent multiple simultaneous signOut calls
        const activeCompanyId = ref<string | null>(null);
        const router = useRouter();
        const toast = useToastStore();

        // Bound once per store instance — guards against double-binding the
        // visibilitychange listener when startCsrfMonitoring() is called repeatedly.
        let visibilityListenerBound = false;
        const onVisibilityChange = (): void => {
            if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
                void validateCsrfToken();
            }
        };

        // Getters
        const isAuthenticated = computed<boolean>(() => !!user.value && !!user.value.email_verified);
        const isProfileCompleted = computed<boolean>(() => !!user.value && !!user.value.profile_complete);
        const activeCompany = computed(() => {
            if (!user.value?.companies || user.value.companies.length === 0) {
                return null;
            }

            // If activeCompanyId is set, find that company
            if (activeCompanyId.value) {
                const found = user.value.companies.find((c) => c.id === activeCompanyId.value);
                if (found) return found;
            }

            // Default to first company
            return user.value.companies[0];
        });

        // Why the last refresh failed. A refresh that fails because the API is
        // deploying says nothing about the session, so validateCsrfToken() must not
        // read it as an expired session (MILO-482). Kept out of the public store
        // surface: refreshCsrfToken() keeps its boolean contract.
        let csrfRefreshBlockedByMaintenance = false;

        // Actions
        const refreshCsrfToken = async (): Promise<boolean> => {
            try {
                await api.auth.csrf();
                csrfRefreshBlockedByMaintenance = false;
                authToken.value = getCookie('XSRF-TOKEN');
                return !!authToken.value;
            } catch (error) {
                log('Failed to refresh CSRF token:', error);
                csrfRefreshBlockedByMaintenance = isMaintenanceError(error);
                return false;
            }
        };

        const validateCsrfToken = async (): Promise<void> => {
            if (!isAuthenticated.value) return;

            if (!isCsrfTokenValid()) {
                log('CSRF token is invalid or missing, attempting refresh');

                // Try to refresh the CSRF token
                const refreshed = await refreshCsrfToken();

                if (!refreshed && csrfRefreshBlockedByMaintenance) {
                    // The API is in maintenance mode (a deploy). The session is still
                    // valid — retry on the next tick instead of signing the user out.
                    log('CSRF token refresh skipped: API in maintenance mode');
                    return;
                }

                if (!refreshed) {
                    log('Failed to refresh CSRF token, signing user out');

                    // signOut(true) preserves the current route as ?redirect= so the
                    // user lands back here after re-login. We deliberately do NOT call
                    // onTokenExpiration() here as well — that would cause a double
                    // router.push() to /login. Show the session-expired toast instead.
                    await signOut(true);
                    toast.error(i18n.global.t('auth.sessionExpired'), 0);
                }
            }
        };

        const startCsrfMonitoring = (): void => {
            if (csrfCheckInterval.value) {
                clearInterval(csrfCheckInterval.value);
            }

            // Check CSRF token validity every 5 minutes
            csrfCheckInterval.value = setInterval(
                async () => {
                    await validateCsrfToken();
                },
                5 * 60 * 1000,
            );

            // Browsers throttle setInterval in background tabs (often pausing it
            // entirely). Re-validate the token immediately when the tab becomes
            // visible again so the first request after a long idle never goes out
            // with a stale CSRF token.
            if (typeof document !== 'undefined' && !visibilityListenerBound) {
                document.addEventListener('visibilitychange', onVisibilityChange);
                visibilityListenerBound = true;
            }
        };

        const stopCsrfMonitoring = (): void => {
            if (csrfCheckInterval.value) {
                clearInterval(csrfCheckInterval.value);
                csrfCheckInterval.value = null;
            }
            if (typeof document !== 'undefined' && visibilityListenerBound) {
                document.removeEventListener('visibilitychange', onVisibilityChange);
                visibilityListenerBound = false;
            }
        };

        const onTokenExpiration = async (showToast?: boolean, redirectPath?: string): Promise<void> => {
            await router.push(
                redirectPath
                    ? { name: RouteNames.Login, query: { redirect: redirectPath } }
                    : { name: RouteNames.Login },
            );

            if (showToast) {
                toast.error(i18n.global.t('auth.sessionExpired'), 0);
            }
        };

        const fetchCurrentUser = async (): Promise<AxiosResponse> => {
            let waitedMs = 0;

            for (let attempt = 1; ; attempt++) {
                try {
                    // skipAuthRedirect: prevents the global 401 interceptor from
                    // triggering signOut() during boot/login on public routes.
                    // init() handles the unauth case explicitly.
                    return await api.get('/api/user', { skipAuthRedirect: true } as any);
                } catch (error) {
                    const waitMs = Math.max(
                        retryAfterMs(error) ?? USER_RATE_LIMIT_FALLBACK_WAIT_MS,
                        USER_RATE_LIMIT_MIN_WAIT_MS,
                    );

                    if (
                        !isRateLimitError(error) ||
                        attempt >= USER_RATE_LIMIT_MAX_ATTEMPTS ||
                        waitedMs + waitMs > USER_RATE_LIMIT_MAX_WAIT_MS
                    ) {
                        throw error;
                    }

                    // For milo-api's own throttle the api layer has shown the
                    // rate-limit notice by now, saying when the user can carry on.
                    log(`Loading the user was rate limited, trying again in ${waitMs} ms`);
                    await new Promise((resolve) => setTimeout(resolve, waitMs));
                    waitedMs += waitMs;
                }
            }
        };

        const init = async (): Promise<User> => {
            try {
                // Migrate the window's storage as well as the worker's: an existing
                // installation must not retain the old unpartitioned API responses.
                await clearPrivateApiCache();
                // Wipe any previous user's in-memory store data before hydrating the
                // new session (this also covers loginAs / stopImpersonating, which
                // re-run init()). auth + syncQueue are intentionally excluded — see
                // RESET_EXCLUDED_STORES in src/pinia.ts.
                $ResetPinia().all();
                const { data } = await fetchCurrentUser();
                user.value = data.data;
                ready.value = true;
                startCsrfMonitoring();

                // Now that a session is confirmed, flush any mutations that were queued
                // offline. processQueue() is self-guarding (auth/online/pending), so this
                // is a no-op when there is nothing to send.
                void useSyncQueueStore().processQueue();

                // Both warm-ups below read the tenant API, which answers an unconfirmed
                // email address with 403 email_not_verified: nothing to warm until then.
                if (isAuthenticated.value) {
                    // Warm the form reference data (BTW codes) for the active company while
                    // we are online, so the create forms still render after going offline.
                    // Fire-and-forget: never throws, no-op when offline or already fresh.
                    // Re-runs per company because a company switch reloads the app.
                    void useReferenceDataStore().warm();

                    // Likewise cache the first page of customers so the customer picker is
                    // populated offline. No-op when offline or already cached.
                    void useCustomersStore().warmOfflineCache();
                }
            } catch (error) {
                log('Failed to initialize auth store:', error);
                ready.value = true;

                // Only the API saying "no session" signs the user out. A 429 that
                // outlasted the wait, a 5xx or a network error leaves the user state
                // and the running quick timer as they are, so a busy minute or a
                // hiccup never reads as a lost session.
                const status = (error as { response?: { status?: number } })?.response?.status;
                if (status === undefined || !SIGNED_OUT_STATUSES.includes(status)) {
                    return user.value as User;
                }

                user.value = null;
                try {
                    if (typeof localStorage !== 'undefined') {
                        localStorage.removeItem(QUICK_TIMER_ACTIVE_SESSION_KEY);
                    }
                } catch (e) {
                    log('Could not remove quick timer active session after failed auth init:', e);
                }
            }

            return user.value as User;
        };

        /**
         * init() after the server switched identity: a login, a 2FA step, the
         * start or end of an impersonation. init() keeps the user in memory when
         * loading fails for a reason other than the session, but here that user
         * is the previous identity, which must never stay in place.
         */
        const initSwitchedSession = async (): Promise<User> => {
            const previous = user.value;
            await init();

            if (previous !== null && user.value === previous) {
                user.value = null;
            }

            return user.value as User;
        };

        const login = async ({
            email,
            password,
        }: {
            email: string;
            password: string;
        }): Promise<User | { requires_2fa: boolean }> => {
            try {
                await api.auth.csrf();
                authToken.value = getCookie('XSRF-TOKEN');

                const res: { data: { requires_2fa: boolean; message: string } } = await api.post('/login', {
                    email,
                    password,
                });

                if (res?.data?.requires_2fa) {
                    log('Login successful but 2FA is required');
                    return Promise.resolve({ requires_2fa: true });
                }

                return initSwitchedSession();
            } catch (error) {
                user.value = null;
                authToken.value = null;
                throw error;
            }
        };

        const signOut = async (includeRedirect = false): Promise<void> => {
            // Prevent multiple simultaneous signOut calls (endless loop protection).
            // The flag is reset only after navigation completes (in finally below)
            // so concurrent callers are blocked for the entire teardown lifecycle.
            if (isSigningOut.value) {
                return;
            }

            isSigningOut.value = true;

            // Capture the current route before clearing state so we can redirect back after login
            const currentRoute = router.currentRoute.value;
            const redirectPath =
                includeRedirect && currentRoute.name !== RouteNames.Login ? currentRoute.fullPath : undefined;

            try {
                // Try to call logout endpoint, but don't fail if session is already expired.
                // skipAuthRedirect prevents the 401 interceptor from triggering another signOut.
                await api.post('/logout', {}, { skipAuthRedirect: true } as any).catch((error) => {
                    // Silently ignore errors (e.g., 401 when session already expired).
                    // We still want to clean up locally.
                    log('Logout endpoint failed (non-fatal):', error);
                });
            } finally {
                // Always clean up local state, regardless of API call success.
                await clearPrivateApiCache();
                stopCsrfMonitoring();
                user.value = null;
                authToken.value = null;
                activeCompanyId.value = null;

                // Reset all Pinia stores to their initial state so a previous user's
                // in-memory data (customers, invoices, catalogs, …) can't linger.
                // auth + syncQueue are excluded — see RESET_EXCLUDED_STORES in
                // src/pinia.ts; both are cleaned up explicitly here instead.
                $ResetPinia().all();

                // The offline sync queue is per-session data: queued mutations belong to
                // the user who created them and must never carry across logout (or to a
                // different user on a shared browser). clearAll() wipes both the in-memory
                // queue and its localStorage copy, so the indicator can't resurface stale
                // items on the login page. (syncQueue is excluded from $ResetPinia().all()
                // above because that reset would not touch its localStorage copy.)
                useSyncQueueStore().clearAll();

                // The persisted offline customer snapshot is PII and company-scoped.
                // It intentionally survives the $ResetPinia().all() above (so an offline
                // cold boot keeps working), so it must be wiped explicitly here on a real
                // logout — a different user on the same browser must not inherit it.
                useCustomersStore().clearOfflineCache();

                // Defense-in-depth: explicitly remove the persisted auth blob so a
                // stale user object can never re-hydrate on the next page load.
                try {
                    if (typeof localStorage !== 'undefined') {
                        localStorage.removeItem(AUTH_PERSIST_KEY);
                        localStorage.removeItem(QUICK_TIMER_ACTIVE_SESSION_KEY);
                    }
                } catch (e) {
                    log('Could not remove persisted auth state:', e);
                }

                // Best-effort cookie cleanup. The Sanctum session cookie is HttpOnly
                // and cannot be cleared from JS — the server-side /logout call above
                // already invalidates it — but XSRF-TOKEN and any other readable
                // cookies are wiped to avoid lingering CSRF tokens after logout.
                try {
                    api.clearCookies?.();
                } catch (e) {
                    log('Could not clear cookies on signOut:', e);
                }

                try {
                    // Navigate to login page, preserving the current route as a redirect target
                    await router.push(
                        redirectPath
                            ? { name: RouteNames.Login, query: { redirect: redirectPath } }
                            : { name: RouteNames.Login },
                    );
                } catch (e) {
                    log('Navigation to login after signOut failed:', e);
                } finally {
                    // Only release the guard once navigation has fully settled,
                    // so a concurrent caller can't race the partial-teardown window.
                    isSigningOut.value = false;
                }
            }
        };

        const isAdmin = (): boolean => {
            return user.value?.role?.name === 'Admin' || false;
        };

        const loadUser = async (): Promise<void> => {
            return api.user.me().then((response: AxiosResponse) => {
                user.value = response.data.data;
            });
        };

        const setActiveCompany = (companyId: string): void => {
            if (!user.value?.companies) {
                log('Cannot set active company: user has no companies');
                return;
            }

            const company = user.value.companies.find((c) => c.id === companyId);
            if (!company) {
                log(`Cannot set active company: company ${companyId} not found`);
                return;
            }

            activeCompanyId.value = companyId;
            log(`Active company set to: ${company.name}`);
        };

        /**
         * Keep the company snapshot in step with a change made elsewhere
         * (MILO-352: uploading or removing terms from the settings screen).
         *
         * `user` is fetched once per app boot, so a settings endpoint that
         * changes a company field leaves this copy stale until a reload — which
         * is how the missing-terms warning kept firing after an upload. Callers
         * patch the field they just changed rather than refetching the user.
         */
        const patchCompany = (companyId: string, changes: Partial<Company>): void => {
            const company = user.value?.companies?.find((c) => c.id === companyId);

            if (!company) {
                log(`Cannot patch company: company ${companyId} not found`);
                return;
            }

            Object.assign(company, changes);
        };

        const loginAs = async (userId: string, reason: string): Promise<void> => {
            try {
                // Call the login-as endpoint
                await api.user.loginAs(userId, reason);

                // Reinitialize the auth state with the new user
                await initSwitchedSession();

                // Navigate to home page
                await router.push({ name: RouteNames.Home });
            } catch (error) {
                log('Failed to login as user:', error);
                // Avoid leaving a half-initialised state where the impersonation
                // request succeeded server-side but local hydration failed.
                // Force a clean signOut so the operator returns to a known state.
                await signOut().catch(() => {
                    /* swallow — primary error is rethrown below */
                });
                throw error;
            }
        };

        const stopImpersonating = async (): Promise<void> => {
            try {
                await api.user.logoutAs();

                // Reinitialize the auth state with the original admin user
                await initSwitchedSession();

                // Navigate to home page
                await router.push({ name: RouteNames.Home });
            } catch (error) {
                log('Failed to stop impersonating:', error);
                // Same rationale as loginAs: never linger in a half-state where
                // the server has switched identity but the client hasn't.
                await signOut().catch(() => {
                    /* swallow — primary error is rethrown below */
                });
                throw error;
            }
        };

        /**
         * Fetches the QR code for setting up two-factor authentication (2FA) for the current user.
         * This QR code can be scanned by an authenticator app (like Google Authenticator) to link the app to the user's account.
         *
         * @returns {Promise<UserTwoFactorSetupResponse>}
         */
        const getTwoFactorSetup = async (): Promise<UserTwoFactorSetupResponse> => {
            try {
                const response = await api.user.setupTwoFactor();
                return response.data;
            } catch (error) {
                log('Failed to get 2FA QR code:', error);
                throw error;
            }
        };

        const verifyTwoFactor = async (code: string, rememberDevice = false): Promise<void> => {
            try {
                await api.auth.verifyTwoFactor(code, rememberDevice);
                await initSwitchedSession(); // Session is now fully authenticated — load user
            } catch (error) {
                log('Failed to verify 2FA token:', error);
                throw error;
            }
        };

        const activateTwoFactor = async (code: string): Promise<string[]> => {
            try {
                const response = await api.user.activateTwoFactor(code);
                // loadUser() is intentionally deferred — calling it here would set
                // two_factor_enabled = true and collapse the wizard before step 4 is shown.
                // The component calls loadUser() via refreshAfterActivation() on "Done".
                const codes = response.data.recovery_codes ?? [];
                // Normalize: API returns { code, used_at }[] objects
                return codes.map((c: string | { code: string }) => (typeof c === 'string' ? c : c.code));
            } catch (error) {
                log('Failed to activate 2FA:', error);
                throw error;
            }
        };

        const deactivateTwoFactor = async (code: string): Promise<void> => {
            try {
                await api.user.deactivateTwoFactor(code);
                await loadUser(); // Refresh user data to reflect 2FA status
            } catch (error) {
                log('Failed to deactivate 2FA:', error);
                throw error;
            }
        };

        return {
            // State
            user,
            authToken,
            ready,
            csrfCheckInterval,
            activeCompanyId,
            // Getters
            isAuthenticated,
            isProfileCompleted,
            activeCompany,
            // Actions
            init,
            refreshCsrfToken,
            validateCsrfToken,
            startCsrfMonitoring,
            stopCsrfMonitoring,
            login,
            signOut,
            loadUser,
            setActiveCompany,
            patchCompany,
            onTokenExpiration,
            getTwoFactorSetup,
            verifyTwoFactor,
            activateTwoFactor,
            deactivateTwoFactor,
            isAdmin,
            loginAs,
            stopImpersonating,
        };
    },
    {
        // SECURITY: only `activeCompanyId` is persisted. The `user` object is NEVER
        // persisted to localStorage — doing so allowed client-side privilege
        // escalation (e.g. tampering with `email_verified`, `profile_complete`,
        // or `role.name = 'Admin'` to bypass router guards). The full user is
        // re-hydrated from `/api/user` on every app boot via `init()`, which is
        // wired up in `router.beforeEach`.
        persist: {
            key: AUTH_PERSIST_KEY,
            pick: ['activeCompanyId'],
            storage: localStorage,
        } as any,
    },
);
